import { Fraction } from 'bi-fraction';
import { H2Pair as H2PairAbi } from '../abis';
import { AGENTFUN_TAX, FUN_TOKEN_DECIMALS, NATIVE_TOKEN_IDENTIFIER, SWAP_DEADLINE_MINUTES, TRADING_FEE_H2, ZKCRO_DECIMALS, } from '../constants';
import { getContract } from '../utils';
import { approveIfRequired } from '../utils/approveIfRequired';
import { executeTx } from '../utils/executeTx';
import { isSigner } from '../utils/isSigner';
export class Swap {
    constructor(options) {
        this.options = options;
        this.getReceiveAmount = async (params) => {
            let amountInBn = params.amountIn.shl(params.inputTokenDecimals).quotient;
            if (params.mode === 'sell-agent' && !params.ignoreFee) {
                amountInBn *= 100n - BigInt(AGENTFUN_TAX * 100);
                amountInBn /= 100n;
            }
            if (amountInBn === 0n)
                return {
                    withSlippage: Fraction.ZERO,
                    withoutSlippage: Fraction.ZERO,
                };
            const path = this.getPath(params.inputTokenAddress, params.outputTokenAddress, params.mode);
            let expectedReceiveAmount = (await this.options.h2router.getAmountsOut(amountInBn, path)).slice(-1)[0];
            if (params.mode === 'buy-agent' && !params.ignoreFee) {
                expectedReceiveAmount *= 100n - BigInt(AGENTFUN_TAX * 100);
                expectedReceiveAmount /= 100n;
            }
            const withoutSlippage = new Fraction(expectedReceiveAmount).shr(params.outputTokenDecimals);
            return {
                withSlippage: withoutSlippage.mul(Fraction.ONE.sub(params.slippage ?? Fraction.ZERO)),
                withoutSlippage,
            };
        };
        this.getPriceImpact = async (params) => {
            if (params.amountIn.lte(0))
                return Fraction.ZERO;
            const { inputTokenDecimals, outputTokenDecimals } = params;
            const path = this.getPath(params.inputTokenAddress, params.outputTokenAddress, params.mode);
            const getTokenDecimals = (i) => this.getTokenDecimals({
                tokenIndexOnPath: i,
                path,
                inputTokenDecimals,
                outputTokenDecimals,
            });
            const reservesPromises = Array(path.length - 1);
            // use those below; run it in parallel
            for (let i = 0; i < path.length - 1; i++) {
                const tokenIn = path[i];
                const tokenOut = path[i + 1];
                reservesPromises[i] = this.getReserves(tokenIn, tokenOut);
            }
            const amountsOutPromise = this.getReceiveAmount({
                amountIn: params.amountIn,
                inputTokenAddress: params.inputTokenAddress,
                inputTokenDecimals,
                outputTokenAddress: params.outputTokenAddress,
                outputTokenDecimals,
                mode: params.mode,
                ignoreFee: true,
            });
            let midPrice = Fraction.ONE;
            for (let i = 0; i < reservesPromises.length; i++) {
                const reserves = await reservesPromises[i];
                const decimalsIn = getTokenDecimals(i);
                const decimalsOut = getTokenDecimals(i + 1);
                const reserveInAdjusted = reserves.reserveA.shr(decimalsIn);
                const reserveOutAdjusted = reserves.reserveB.shr(decimalsOut);
                const hopMidPrice = reserveOutAdjusted.div(reserveInAdjusted);
                midPrice = midPrice.mul(hopMidPrice);
            }
            const idealOutput = params.amountIn.mul(midPrice);
            const amountsOut = (await amountsOutPromise).withoutSlippage;
            const priceImpact = Fraction.ONE.sub(amountsOut.div(idealOutput)).sub(TRADING_FEE_H2);
            return priceImpact;
        };
        this.getPath = (_inputTokenAddress, outputTokenAddress, mode) => {
            const inputTokenAddress = _inputTokenAddress === NATIVE_TOKEN_IDENTIFIER
                ? this.options.addresses.WZkCRO
                : _inputTokenAddress;
            if (inputTokenAddress === this.options.addresses.AgentFun ||
                outputTokenAddress === this.options.addresses.AgentFun)
                return [inputTokenAddress, outputTokenAddress];
            if (inputTokenAddress === this.options.addresses.WZkCRO ||
                outputTokenAddress === this.options.addresses.WZkCRO) {
                return [
                    inputTokenAddress,
                    this.options.addresses.AgentFun,
                    outputTokenAddress,
                ];
            }
            if (mode === 'buy-agent')
                return [
                    inputTokenAddress,
                    this.options.addresses.WZkCRO,
                    this.options.addresses.AgentFun,
                    outputTokenAddress,
                ];
            return [
                inputTokenAddress,
                this.options.addresses.AgentFun,
                this.options.addresses.WZkCRO,
                outputTokenAddress,
            ];
        };
    }
    async swap(params) {
        const { withSlippage } = await this.getReceiveAmount(params);
        if (params.inputTokenAddress === NATIVE_TOKEN_IDENTIFIER)
            return this.swapNativeToken({
                ...params,
                minimumReceiveAmount: withSlippage,
            });
        if (!isSigner(this.options.signerOrProvider))
            throw new Error('need signer for swap function');
        const amountInBn = params.amountIn.shl(params.inputTokenDecimals).quotient;
        const path = this.getPath(params.inputTokenAddress, params.outputTokenAddress, params.mode);
        const deadline = Math.floor(Date.now() / 1000) + 60 * SWAP_DEADLINE_MINUTES;
        await approveIfRequired(params.inputTokenAddress, await this.options.h2router.getAddress(), amountInBn, this.options.signerOrProvider);
        const minimumReceiveAmountBn = withSlippage.shl(params.outputTokenDecimals).quotient;
        return executeTx(this.options.h2router.swapExactTokensForTokensSupportingFeeOnTransferTokens(amountInBn, minimumReceiveAmountBn, path, this.options.signerOrProvider.getAddress(), deadline));
    }
    async swapNativeToken(params) {
        if (!isSigner(this.options.signerOrProvider))
            throw new Error('need signer for swapNativeToken function');
        const amountInBn = params.amountIn.shl(ZKCRO_DECIMALS).quotient;
        const path = this.getPath(this.options.addresses.WZkCRO, params.outputTokenAddress, params.mode);
        const deadline = Math.floor(Date.now() / 1000) + 60 * SWAP_DEADLINE_MINUTES;
        const minimumReceiveAmountBn = params.minimumReceiveAmount.shl(params.outputTokenDecimals).quotient;
        return executeTx(this.options.h2router.swapExactETHForTokensSupportingFeeOnTransferTokens(minimumReceiveAmountBn, path, this.options.signerOrProvider.getAddress(), deadline, { value: amountInBn }));
    }
    async getPairAddress(tokenA, tokenB) {
        const pairAddress = await this.options.h2factory.getPair(tokenA, tokenB);
        if (pairAddress === '0x0000000000000000000000000000000000000000') {
            throw new Error('Pair does not exist for these tokens.');
        }
        return pairAddress;
    }
    async getReserves(tokenA, tokenB) {
        const pairAddress = await this.getPairAddress(tokenA, tokenB);
        const pairContract = getContract(pairAddress, H2PairAbi, this.options.signerOrProvider);
        const [reservesData, token0Address, token1Address] = await Promise.all([
            pairContract.getReserves(),
            pairContract.token0(),
            pairContract.token1(),
        ]);
        const token0 = token0Address.toLowerCase();
        const token1 = token1Address.toLowerCase();
        const reserve0 = reservesData._reserve0;
        const reserve1 = reservesData._reserve1;
        let reserveA, reserveB;
        if (tokenA.toLowerCase() === token0 && tokenB.toLowerCase() === token1) {
            reserveA = reserve0;
            reserveB = reserve1;
        }
        else if (tokenA.toLowerCase() === token1 &&
            tokenB.toLowerCase() === token0) {
            reserveA = reserve1;
            reserveB = reserve0;
        }
        else {
            throw new Error("Token addresses do not match the pair's tokens");
        }
        return {
            reserveA: new Fraction(reserveA),
            reserveB: new Fraction(reserveB),
        };
    }
    getTokenDecimals(params) {
        if (params.tokenIndexOnPath === 0)
            return params.inputTokenDecimals;
        if (params.tokenIndexOnPath === params.path.length - 1)
            return params.outputTokenDecimals;
        return FUN_TOKEN_DECIMALS;
    }
}
